import { Injectable } from '@angular/core';
import { BeneficiarySelection, BeneficiaryState } from '@models/beneficiary.model';

/**
 * Manages DOB-based duplicate detection for beneficiaries.
 * Maintains an incremental Map<dateOfBirth, BeneficiarySelection> for O(1) lookups.
 */
@Injectable({
  providedIn: 'root',
})
export class BeneficiaryDuplicateDetectionService {
  private dobMap = new Map<string, BeneficiarySelection>();

  /** Full rebuild — only called once on init when restoring persisted state. */
  rebuildFromState(state: BeneficiaryState): void {
    this.dobMap.clear();
    for (const bene of state.primaryBeneficiaries) {
      this.add(bene);
    }
    for (const bene of state.backupBeneficiaries) {
      this.add(bene);
    }
  }

  add(bene: BeneficiarySelection): void {
    if (bene.dateOfBirth) this.dobMap.set(bene.dateOfBirth, bene);
  }

  remove(bene: BeneficiarySelection): void {
    if (bene.dateOfBirth) this.dobMap.delete(bene.dateOfBirth);
  }

  findByDob(bene: BeneficiarySelection): BeneficiarySelection | null {
    return this.dobMap.get(bene.dateOfBirth) ?? null;
  }

  /** Check if a beneficiary with the same householdMemberId already exists in a section. */
  isDuplicateInSection(bene: BeneficiarySelection, section: BeneficiarySelection[]): boolean {
    return !!bene.householdMemberId && section.some(b => b.householdMemberId === bene.householdMemberId);
  }

  clear(): void {
    this.dobMap.clear();
  }
}
